import { useContext, useState, ReactNode } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormLabel from "@mui/material/FormLabel";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import Popover from "@mui/material/Popover";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import FilterListIcon from "@mui/icons-material/FilterList";
import {
  AffiliatedSchoolKey,
  affiliatedSchools,
  Filters,
} from "../types";
import {
  ActionType,
  ContextFilter,
  contextFilterLabels,
  DataContext,
} from "../DataContextProvider";

// Issue #143 sub-step 6: React 18 made `React.FC`'s implicit
// `children` opt-in -- components that take JSX children must declare
// it explicitly. Same pattern iter 22/23/25 applied to
// `LanguageContextProvider`, `DataContextProvider`, `Collapsible`,
// `ApplicantHeaderWrapper`. `FilterRow` is the last child-accepting
// wrapper in the components/ tree.
interface FilterRowProps {
  title: string;
  children?: ReactNode;
}

interface ListToolbarProps {
  filters: Filters;
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
}

const FilterRow: React.FC<FilterRowProps> = ({ title, children }) => {
  return (
    <ListItem>
      <Typography align="right" style={{ width: "6rem" }}>
        {title}:
      </Typography>
      <Box style={{ flex: 1, padding: "0 .5rem" }}>{children}</Box>
    </ListItem>
  );
};

const ListToolbar: React.FC<ListToolbarProps> = ({ filters, setFilters }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const {
    state: { data, filter: contextFilter },
    dispatch,
  } = useContext(DataContext);

  const setSelectedHighSchool = (highSchool: string | null) => {
    setFilters((filters) => ({ ...filters, highSchool }));
  };

  const setSelectedAffiliatedSchools = (schools: string[] | null) => {
    if (!schools) {
      setFilters((filters) => ({
        ...filters,
        ...Object.keys(affiliatedSchools).reduce(
          (acc, key) => ({ ...acc, [key]: null }),
          {}
        ),
      }));
    } else {
      setFilters((filters) => ({
        ...filters,
        ...Object.keys(affiliatedSchools).reduce(
          (acc, key) => ({
            ...acc,
            [key]: schools.includes(key) ? true : null,
          }),
          {}
        ),
      }));
    }
  };

  return (
    <Toolbar>
      <Box flex={1}>
        <Stack direction="row" spacing={5} alignItems="center">
          <Typography variant="h6">出願者一覧</Typography>
          {/* {contextFilter && <Stack direction="row" alignItems="center" spacing={2}>
                        <Typography>表示対象: </Typography>
                        <Select
                            variant="standard"
                            labelId="context-filter-label"
                            value={contextFilter}
                            onChange={e => dispatch && dispatch({ type: ActionType.SET_FILTER, payload: e.target.value })}
                        >
                            <MenuItem value="all" selected>全出願者</MenuItem>
                            <MenuItem value="complete">出願完了者</MenuItem>
                            <MenuItem value="screening">書類審査合格者</MenuItem>
                        </Select>
                    </Stack>} */}
          {contextFilter && (
            <FormControl component="fieldset">
              <Stack direction="row" alignItems="center" spacing={2}>
                <FormLabel component="legend">表示対象</FormLabel>
                <RadioGroup
                  row
                  value={contextFilter}
                  onChange={(e) => {
                    if (dispatch)
                      dispatch({
                        type: ActionType.SET_FILTER,
                        payload: e.target.value as ContextFilter,
                      });
                    localStorage.setItem("ContextFilter", e.target.value);
                  }}
                >
                  {Object.entries(contextFilterLabels).map(([key, value]) => (
                    <FormControlLabel
                      key={`contextFilter-${key}`}
                      value={key}
                      control={<Radio size="small" />}
                      disableTypography
                      label={
                        <Typography
                          color={contextFilter === key ? "primary" : "inherit"}
                        >
                          {value}
                        </Typography>
                      }
                    />
                  ))}
                </RadioGroup>
              </Stack>
            </FormControl>
          )}
        </Stack>
      </Box>
      <Tooltip title="Filter">
        <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
          <FilterListIcon />
        </IconButton>
      </Tooltip>
      <Popover
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        anchorEl={anchorEl}
        PaperProps={{ style: { width: "48rem" } }}
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
      >
        <List>
          <ListItem>
            <Typography variant="h6" style={{ flex: 1 }}>
              Filter
            </Typography>
            <Button
              color="error"
              disabled={
                Object.entries(filters).filter(([key, val]) => val !== null)
                  .length === 0
              }
              onClick={() => setFilters({})}
            >
              Clear
            </Button>
          </ListItem>
          <Box padding="0 .5rem">
            {/* <FilterRow title="第1志望">
                            <Stack direction="row" spacing={1}>
                                <Chip
                                    label="奨学金１"
                                    onClick={() => setSelectedScholarshipFirst(filters.scholarshipFirst === '奨学金１' ? null : '奨学金１')}
                                    color={filters.scholarshipFirst === '奨学金１' ? 'primary' : 'default'}
                                    variant={filters.scholarshipFirst === '奨学金１' ? 'filled' : 'outlined'}
                                />
                                <Chip
                                    label="奨学金２"
                                    onClick={() => setSelectedScholarshipFirst(filters.scholarshipFirst === '奨学金２' ? null : '奨学金２')}
                                    color={filters.scholarshipFirst === '奨学金２' ? 'primary' : 'default'}
                                    variant={filters.scholarshipFirst === '奨学金２' ? 'filled' : 'outlined'}
                                />
                                <Chip
                                    label="奨学金３"
                                    onClick={() => setSelectedScholarshipFirst(filters.scholarshipFirst === '奨学金３' ? null : '奨学金３')}
                                    color={filters.scholarshipFirst === '奨学金３' ? 'primary' : 'default'}
                                    variant={filters.scholarshipFirst === '奨学金３' ? 'filled' : 'outlined'}
                                />
                            </Stack>
                        </FilterRow> */}
            <FilterRow title="高校名">
              <Box flex={1} padding="0 1rem">
                <Autocomplete
                  options={[
                    ...new Set(data.map(({ highSchool }) => highSchool)),
                  ]}
                  onChange={(event, value) =>
                    setSelectedHighSchool(value ?? null)
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="高校名"
                      variant="standard"
                    />
                  )}
                />
              </Box>
            </FilterRow>
            <FilterRow title="提携校志望">
              <ToggleButtonGroup
                fullWidth
                value={Object.keys(filters).filter(
                  (key) =>
                    Object.keys(affiliatedSchools).includes(key) &&
                    filters[key as AffiliatedSchoolKey]
                )}
                onChange={(event, value) => setSelectedAffiliatedSchools(value)}
              >
                {Object.entries(affiliatedSchools).map(([key, { symbol }]) => (
                  <ToggleButton
                    key={`toggleButton-${key}`}
                    value={key}
                    color={
                      filters[key as AffiliatedSchoolKey]
                        ? "primary"
                        : "standard"
                    }
                  >
                    <Typography style={{ fontWeight: "bold" }}>
                      {symbol}
                    </Typography>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </FilterRow>
            {/* <FilterRow title="Tags">
              <Box flex={1} padding="0 1rem">
                <Autocomplete
                  multiple
                  options={[
                    ...new Set(
                      data.reduce<string[]>(
                        (acc, { tags }) => [...acc, ...tags],
                        []
                      )
                    ),
                  ]}
                  value={Array.isArray(filters.tags) ? [...filters.tags] : []}
                  onChange={(event, value: string[]) => setSelectedTags(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder="Tags"
                      variant="standard"
                    />
                  )}
                />
              </Box>
            </FilterRow> */}
          </Box>
        </List>
      </Popover>
    </Toolbar>
  );
};

export default ListToolbar;
